import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Bot, Eye, MousePointerClick, ShoppingBag, Clock, Crown, Send, Loader2, Megaphone, MessageSquare, Users, CheckCircle, XCircle, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { acText } from './adsCommercialI18n';

const Stat = ({ icon: Icon, label, d7, d30, total, L, testid }) => (
  <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid={testid}>
    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><Icon className="w-4 h-4 text-fuchsia-400" />{label}</div>
    <div className="text-3xl font-bold text-white">{total}</div>
    <div className="text-[11px] text-gray-400 mt-1">{L.d7}: <span className="text-white">{d7}</span> · {L.d30}: <span className="text-white">{d30}</span></div>
  </div>
);

const Toggle = ({ on, onChange, label, desc, testid, disabled }) => (
  <button type="button" onClick={() => !disabled && onChange(!on)} disabled={disabled}
    className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${on ? 'bg-fuchsia-500/15 border-fuchsia-500/40' : 'bg-white/5 border-white/10'} ${disabled ? 'opacity-50' : 'hover:bg-white/10'}`}
    data-testid={testid}>
    <div className={`mt-0.5 w-9 h-5 rounded-full relative shrink-0 transition-colors ${on ? 'bg-fuchsia-500' : 'bg-white/20'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </div>
    <div className="min-w-0"><div className="text-sm font-semibold text-white">{label}</div>{desc && <div className="text-[11px] text-gray-400">{desc}</div>}</div>
  </button>
);

const Timeline = ({ data, L }) => {
  const max = Math.max(1, ...data.map(d => d.profile_view + d.link_click + d.shop_view));
  return (
    <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="ads-commercial-timeline">
      <div className="text-xs text-gray-400 mb-3">{L.timeline}</div>
      <div className="flex items-end gap-1 h-20">
        {data.map(d => {
          const v = d.profile_view + d.link_click + d.shop_view;
          return <div key={d.date} title={`${d.date}: ${v}`} className="flex-1 rounded-t bg-gradient-to-t from-fuchsia-600 to-violet-400" style={{ height: `${Math.max(4, (v / max) * 100)}%` }} />;
        })}
      </div>
    </div>
  );
};

export const AdsCommercialPanel = () => {
  const { language } = useLanguage();
  const L = acText(language);
  const [searchParams] = useSearchParams();
  const [analytics, setAnalytics] = useState(null);
  const [sub, setSub] = useState(null);
  const [agent, setAgent] = useState(null);
  const [targets, setTargets] = useState(null);
  const [report, setReport] = useState(null);
  const [saving, setSaving] = useState(false);
  const [prospecting, setProspecting] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const sessionRef = useRef(null);
  const chatEndRef = useRef(null);

  const load = async () => {
    const [a, s, g, t, r, c] = await Promise.allSettled([
      axios.get('/api/ads-commercial/analytics'),
      axios.get('/api/ads-commercial/subscription', { params: { lang: language } }),
      axios.get('/api/ads-commercial/agent'),
      axios.get('/api/ads-commercial/agent/prospect/targets'),
      axios.get('/api/ads-commercial/agent/report'),
      axios.get('/api/ads-commercial/agent/chat'),
    ]);
    if (a.status === 'fulfilled') setAnalytics(a.value.data);
    if (s.status === 'fulfilled') setSub(s.value.data);
    if (g.status === 'fulfilled') setAgent(g.value.data);
    if (t.status === 'fulfilled') setTargets(t.value.data);
    if (r.status === 'fulfilled') setReport(r.value.data);
    if (c.status === 'fulfilled') {
      const items = c.value.data.items || [];
      setChat(items);
      if (items.length) sessionRef.current = items[items.length - 1].session_id;
    }
  };

  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (searchParams.get('ads_premium') === 'success' && sid) {
      axios.post('/api/ads-commercial/subscription/confirm', { session_id: sid }).then(() => { toast.success(L.active); load(); }).catch(() => load());
    } else load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat]);

  const hasAccess = !!sub?.active;

  const subscribe = async () => {
    try {
      const r = await axios.post('/api/ads-commercial/subscription/checkout', { lang: language });
      const { url, session_id } = r.data;
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url, presentationStyle: 'popover' });
        let attempts = 0;
        const iv = setInterval(async () => {
          attempts += 1;
          try {
            const c = await axios.post('/api/ads-commercial/subscription/confirm', { session_id });
            if (c.data.active) {
              clearInterval(iv);
              try { await Browser.close(); } catch (_) {}
              toast.success(L.active);
              load();
            }
          } catch (_) { /* keep polling */ }
          if (attempts > 100) clearInterval(iv);
        }, 3000);
        return;
      }
      window.location.href = url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Stripe'); }
  };
  const cancel = async () => {
    if (!window.confirm(L.cancelConfirm)) return;
    await axios.post('/api/ads-commercial/subscription/cancel');
    toast.success(L.inactive); load();
  };

  const saveAgent = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put('/api/ads-commercial/agent', patch);
      setAgent(r.data);
      toast.success(L.saved);
    } catch (e) { toast.error(e.response?.status === 402 ? L.premiumRequired : (e.response?.data?.detail || 'Erreur')); }
    finally { setSaving(false); }
  };

  const runProspect = async () => {
    setProspecting(true);
    try {
      const r = await axios.post('/api/ads-commercial/agent/prospect/run');
      toast.success(`${r.data.posted} ${L.posted}`);
      load();
    } catch (e) { toast.error(e.response?.status === 402 ? L.premiumRequired : (e.response?.data?.detail || 'Erreur')); }
    finally { setProspecting(false); }
  };

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    setChatInput('');
    setChat(c => [...c, { id: `tmp-${Date.now()}`, role: 'user', content: msg }]);
    setChatBusy(true);
    try {
      const r = await axios.post('/api/ads-commercial/agent/chat', { message: msg, session_id: sessionRef.current, lang: language });
      sessionRef.current = r.data.session_id;
      setChat(c => [...c, { id: `a-${Date.now()}`, role: 'assistant', content: r.data.reply }]);
    } catch (e) { toast.error(e.response?.status === 402 ? L.premiumRequired : 'Erreur'); }
    finally { setChatBusy(false); }
  };

  if (!analytics || !sub || !agent) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div>;

  const inputCls = 'w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/60';

  return (
    <div className="space-y-5" data-testid="ads-commercial-panel">
      <div className="p-5 rounded-3xl bg-gradient-to-br from-fuchsia-600/30 via-violet-700/20 to-black border border-fuchsia-500/30">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-fuchsia-500/30 flex items-center justify-center"><Bot className="w-7 h-7 text-fuchsia-200" /></div>
          <div><h2 className="text-xl font-bold text-white">{L.title}</h2><p className="text-sm text-gray-300">{L.subtitle}</p></div>
        </div>
      </div>

      {/* Analytics */}
      <section>
        <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wide mb-3">{L.analytics}</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Eye} label={L.profileViews} {...analytics.profile_view} L={L} testid="ac-stat-profile-views" />
          <Stat icon={MousePointerClick} label={L.linkClicks} {...analytics.link_click} L={L} testid="ac-stat-link-clicks" />
          <Stat icon={ShoppingBag} label={L.shopViews} {...analytics.shop_view} L={L} testid="ac-stat-shop-views" />
          <div className="p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="ac-stat-shop-time">
            <div className="flex items-center gap-2 text-gray-400 text-xs mb-2"><Clock className="w-4 h-4 text-fuchsia-400" />{L.avgTime}</div>
            <div className="text-3xl font-bold text-white">{analytics.shop_time.avg_seconds}<span className="text-base text-gray-400 ml-1">{L.seconds}</span></div>
            <div className="text-[11px] text-gray-400 mt-1">{L.followers}: <span className="text-white">{analytics.followers}</span></div>
          </div>
        </div>
        <div className="mt-3"><Timeline data={analytics.timeline} L={L} /></div>
      </section>

      {/* Subscription */}
      <section className="p-5 rounded-2xl bg-amber-500/10 border border-amber-500/30" data-testid="ac-subscription-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <Crown className="w-6 h-6 text-amber-300 shrink-0 mt-0.5" />
            <div><h3 className="font-bold text-white">{L.premiumTitle}</h3><p className="text-xs text-gray-300 mt-1">{L.premiumDesc}</p></div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-200" data-testid="ac-price">{sub.price.amount} {sub.price.symbol}<span className="text-xs text-gray-400 font-normal"> {L.perMonth}</span></div>
            <div className={`text-xs mt-1 flex items-center gap-1 justify-end ${hasAccess ? 'text-emerald-300' : 'text-gray-400'}`} data-testid="ac-sub-status">
              {hasAccess ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {sub.is_admin && !sub.subscription ? L.adminFree : hasAccess ? L.active : L.inactive}
            </div>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          {!sub.enabled && <span className="text-xs text-amber-200/80">{L.disabledByAdmin}</span>}
          {sub.enabled && !sub.subscription && !sub.is_admin && (
            <button onClick={subscribe} className="px-4 py-2 rounded-full bg-amber-400 text-black font-semibold text-sm hover:bg-amber-300" data-testid="ac-subscribe-btn">{L.subscribe}</button>
          )}
          {sub.subscription && (
            <button onClick={cancel} className="px-4 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-red-500/30" data-testid="ac-cancel-btn">{L.cancelSub}</button>
          )}
        </div>
      </section>

      {/* Agent config */}
      <section className={`p-5 rounded-2xl bg-white/5 border border-white/10 space-y-3 ${!hasAccess ? 'opacity-70' : ''}`} data-testid="ac-agent-card">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-fuchsia-300 shrink-0 mt-0.5" />
          <div><h3 className="font-bold text-white">{L.agentTitle}</h3><p className="text-xs text-gray-400">{L.agentDesc}</p></div>
        </div>
        {!hasAccess && <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2" data-testid="ac-premium-required">{L.premiumRequired}</div>}
        <Toggle on={!!agent.enabled} onChange={v => saveAgent({ enabled: v })} label={L.enabled} testid="ac-agent-enabled" disabled={!hasAccess} />
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder={L.businessName} value={agent.business_name || ''} onChange={e => setAgent({ ...agent, business_name: e.target.value })} disabled={!hasAccess} data-testid="ac-business-name" />
          <input className={inputCls} placeholder={L.tone} value={agent.tone || ''} onChange={e => setAgent({ ...agent, tone: e.target.value })} disabled={!hasAccess} data-testid="ac-tone" />
          <textarea className={`${inputCls} sm:col-span-2`} rows={2} placeholder={L.description} value={agent.description || ''} onChange={e => setAgent({ ...agent, description: e.target.value })} disabled={!hasAccess} data-testid="ac-description" />
          <textarea className={`${inputCls} sm:col-span-2`} rows={2} placeholder={L.offers} value={agent.offers || ''} onChange={e => setAgent({ ...agent, offers: e.target.value })} disabled={!hasAccess} data-testid="ac-offers" />
          <input className={inputCls} placeholder={L.audience} value={agent.audience || ''} onChange={e => setAgent({ ...agent, audience: e.target.value })} disabled={!hasAccess} data-testid="ac-audience" />
          <input className={inputCls} placeholder={L.signature} value={agent.signature || ''} onChange={e => setAgent({ ...agent, signature: e.target.value })} disabled={!hasAccess} data-testid="ac-signature" />
        </div>
        <button onClick={() => saveAgent({ business_name: agent.business_name, description: agent.description, offers: agent.offers, audience: agent.audience, tone: agent.tone, signature: agent.signature })}
          disabled={!hasAccess || saving} className="px-4 py-2 rounded-full bg-fuchsia-500 text-white text-sm font-semibold hover:bg-fuchsia-400 disabled:opacity-50" data-testid="ac-save-agent">
          {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : L.save}
        </button>
        <div className="grid sm:grid-cols-3 gap-2 pt-1">
          <Toggle on={!!agent.auto_reply} onChange={v => saveAgent({ auto_reply: v })} label={L.autoReply} desc={L.autoReplyDesc} testid="ac-auto-reply" disabled={!hasAccess} />
          <Toggle on={!!agent.auto_accept_invitations} onChange={v => saveAgent({ auto_accept_invitations: v })} label={L.autoAccept} desc={L.autoAcceptDesc} testid="ac-auto-accept" disabled={!hasAccess} />
          <Toggle on={!!agent.prospect_enabled} onChange={v => saveAgent({ prospect_enabled: v })} label={L.prospect} desc={L.prospectDesc} testid="ac-prospect" disabled={!hasAccess} />
        </div>
        <div className="rounded-xl bg-black/30 border border-white/10 p-3" data-testid="ac-prospect-targets">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold text-gray-300 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{L.targets} ({targets?.groups?.length || 0})</div>
            <button onClick={runProspect} disabled={!hasAccess || !agent.enabled || !agent.prospect_enabled || prospecting || !(targets?.groups?.length)}
              className="px-3 py-1.5 rounded-full bg-violet-500 text-white text-xs font-semibold disabled:opacity-40 flex items-center gap-1" data-testid="ac-run-prospect">
              {prospecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}{L.runProspect}
            </button>
          </div>
          {targets?.groups?.length ? (
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {targets.groups.map(g => (
                <div key={g.id} className="text-xs text-gray-300 flex justify-between gap-2"><span className="truncate">{g.name} · {g.members}</span>{g.cooldown && <span className="text-amber-300 shrink-0">{L.cooldown}</span>}</div>
              ))}
            </div>
          ) : <div className="text-xs text-gray-500">{L.noTargets}</div>}
        </div>
      </section>

      {/* Report */}
      <section className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid="ac-report">
        <h3 className="font-bold text-white mb-3">{L.report}</h3>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[[L.replies, report?.counts?.replies], [L.accepted, report?.counts?.invitations_accepted], [L.prospects, report?.counts?.prospects]].map(([lab, v]) => (
            <div key={lab} className="p-3 rounded-xl bg-black/30 text-center"><div className="text-xl font-bold text-white">{v || 0}</div><div className="text-[10px] text-gray-400">{lab}</div></div>
          ))}
        </div>
        {report?.logs?.length ? (
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {report.logs.map(l => (
              <div key={l.id} className="text-xs p-2 rounded-lg bg-black/30 border border-white/5">
                <span className="text-fuchsia-300">{L[`kind_${l.kind}`] || l.kind}</span> <span className="text-white font-semibold">{l.target_name}</span>
                <span className="text-gray-500"> · {new Date(l.created_at).toLocaleDateString()}</span>
                <div className="text-gray-400 truncate">{l.preview}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-xs text-gray-500">{L.noLogs}</div>}
      </section>

      {/* Chat */}
      <section className="p-5 rounded-2xl bg-gradient-to-br from-violet-900/40 to-black border border-violet-500/30" data-testid="ac-chat">
        <div className="flex items-center gap-2 mb-1"><MessageSquare className="w-5 h-5 text-violet-300" /><h3 className="font-bold text-white">{L.chatTitle}</h3></div>
        <p className="text-xs text-gray-400 mb-3">{L.chatIntro}</p>
        <div className="max-h-72 overflow-y-auto space-y-2 mb-3" data-testid="ac-chat-messages">
          {chat.map(m => (
            <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-fuchsia-500 text-white' : 'bg-white/10 text-gray-100'}`}>{m.content}</div>
            </div>
          ))}
          {chatBusy && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-white/10"><Loader2 className="w-4 h-4 animate-spin text-violet-300" /></div></div>}
          <div ref={chatEndRef} />
        </div>
        <div className="flex gap-2">
          <input className={inputCls} placeholder={L.chatPlaceholder} value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendChat()} disabled={!hasAccess} data-testid="ac-chat-input" />
          <button onClick={sendChat} disabled={!hasAccess || chatBusy || !chatInput.trim()} className="px-4 rounded-xl bg-violet-500 text-white disabled:opacity-40" data-testid="ac-chat-send"><Send className="w-4 h-4" /></button>
        </div>
      </section>
    </div>
  );
};

export default AdsCommercialPanel;
